import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { courses } from '../data/constants';
import {
    fadeInUp,
    staggerContainer,
    staggerCards,
    cardHover,
    floatingAnimation
} from '../data/constants';
import AnimatedSection from './AnimatedSection';
import type { Course } from '../types';

interface CoursesProps {
    onEnrollClick?: (courseId: string) => void;
}

const Courses: React.FC<CoursesProps> = ({ onEnrollClick }) => {
    const [selectedCategory, setSelectedCategory] = useState<'all' | 'music' | 'yoga' | 'dance'>('all');
    console.log(selectedCategory === 'all');
    const filteredCourses = selectedCategory === 'all'
        ? courses
        : courses.filter(course => course.category === selectedCategory);

    const handleEnrollClick = (courseId: string) => {
        if (onEnrollClick) {
            onEnrollClick(courseId);
        } else {
            // Default behavior - scroll to pricing
            const pricingSection = document.getElementById('pricing');
            if (pricingSection) {
                pricingSection.scrollIntoView({ behavior: 'smooth' });
            }
        }
    };

    const CategoryButton: React.FC<{
        category: 'all' | 'music' | 'yoga' | 'dance';
        label: string;
    }> = ({ category, label }) => (
        <motion.button
            onClick={() => setSelectedCategory(category)}
            whileHover={{
                scale: 1.05,
                y: -2
            }}
            whileTap={{ scale: 0.95 }}
            animate={selectedCategory === category ? floatingAnimation : {}}
            className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${selectedCategory === category
                ? 'bg-brand-dark-red text-white shadow-lg'
                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
        >
            {label}
        </motion.button>
    );

    const CourseCard: React.FC<{ course: Course; index: number }> = ({ course, index }) => (
        <motion.div
            variants={cardHover}
            initial="rest"
            whileHover="hover"
            className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 group relative overflow-hidden"
        >
            {/* Animated background gradient on hover */}
            <motion.div
                className="absolute inset-0 bg-gradient-to-br from-brand-dark-red/5 to-brand-gold/5 opacity-0"
                whileHover={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
            />

            {/* Course Image with enhanced animations */}
            <div className="relative mb-6 overflow-hidden rounded-lg">
                <motion.img
                    src={course.icon}
                    alt={`${course.title} classes`}
                    className="w-full h-48 object-cover"
                    loading="lazy"
                    whileHover={{
                        scale: 1.1,
                        rotate: 1
                    }}
                    transition={{
                        duration: 0.4,
                        ease: "easeOut"
                    }}
                />
                {course.isPopular && (
                    <motion.div
                        className="absolute top-4 right-4 bg-brand-gold text-gray-900 px-3 py-1 rounded-full text-sm font-medium"
                        animate={{
                            scale: [1, 1.1, 1],
                            rotate: [0, 5, 0]
                        }}
                        transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut"
                        }}
                    >
                        Popular
                    </motion.div>
                )}

                {/* Floating icon overlay */}
                <motion.div
                    className="absolute top-4 left-4 w-12 h-12 bg-white/90 rounded-full flex items-center justify-center"
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{
                        delay: index * 0.1,
                        type: "spring",
                        stiffness: 200
                    }}
                    whileHover={{
                        scale: 1.2,
                        rotate: 360
                    }}
                >
                    {course.category === 'music' && '🎵'}
                    {course.category === 'yoga' && '🧘'}
                    {course.category === 'dance' && '💃'}
                </motion.div>
            </div>

            {/* Course Content with stagger animations */}
            <motion.div
                className="space-y-4 relative z-10"
                variants={staggerContainer}
                initial="hidden"
                animate="visible"
            >
                <motion.div variants={fadeInUp}>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 font-cinzel">
                        {course.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                        {course.description}
                    </p>
                </motion.div>

                {/* Course Details with enhanced animations */}
                <motion.div
                    className="space-y-3"
                    variants={fadeInUp}
                >
                    {[
                        { label: 'Age Range', value: course.ageRange },
                        { label: 'Duration', value: course.duration },
                        { label: 'Instructor', value: course.instructor }
                    ].map((detail) => (
                        <div key={detail.label} className="flex items-center text-gray-600">
                            <span className="w-2 h-2 bg-brand-gold rounded-full mr-3 flex-shrink-0"></span>
                            <span className="text-sm">{detail.value}</span>
                        </div>
                    ))}
                </motion.div>

                {/* Topics with stagger animation */}
                <motion.div variants={fadeInUp}>
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Topics Covered:</h4>
                    <motion.div
                        className="flex flex-wrap gap-2"
                        variants={staggerContainer}
                        initial="hidden"
                        animate="visible"
                    >
                        {course.topics.map((topic) => (
                            <span
                                key={topic}
                                className="px-3 py-1 bg-brand-gold/20 text-brand-dark-red rounded-full text-sm font-medium hover:bg-brand-gold/30 transition-colors duration-200"
                            >
                                {topic}
                            </span>
                        ))}
                    </motion.div>
                </motion.div>

                {/* Features with check mark animations */}
                <motion.div variants={fadeInUp}>
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Features:</h4>
                    <ul className="space-y-1">
                        {course.features.slice(0, 3).map((feature, featureIndex) => (
                            <motion.li
                                key={featureIndex}
                                className="flex items-center text-sm text-gray-600"
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: featureIndex * 0.1 }}
                                whileHover={{ x: 5 }}
                            >
                                <motion.svg
                                    className="w-4 h-4 text-brand-dark-red mr-2 flex-shrink-0"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{
                                        delay: featureIndex * 0.1 + 0.2,
                                        type: "spring",
                                        stiffness: 400
                                    }}
                                >
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </motion.svg>
                                {feature}
                            </motion.li>
                        ))}
                    </ul>
                </motion.div>

                {/* Enhanced Pricing and CTA */}
                <motion.div
                    className="pt-4 border-t border-gray-100"
                    variants={fadeInUp}
                >
                    <div className="flex items-center justify-between mb-4">
                        <motion.div
                            whileHover={{ scale: 1.05 }}
                        >
                            <span className="text-2xl font-bold text-gray-900">
                                {course.currency}{course.price.toLocaleString()}
                            </span>
                            <span className="text-sm text-gray-500 ml-1">
                                for {course.classCount} classes
                            </span>
                        </motion.div>
                        <motion.div
                            className="text-right"
                            whileHover={{ scale: 1.05 }}
                        >
                            <div className="text-sm text-gray-500">Level</div>
                            <div className="text-sm font-medium text-gray-900 capitalize">
                                {course.level}
                            </div>
                        </motion.div>
                    </div>

                    <motion.button
                        onClick={() => handleEnrollClick(course.id)}
                        whileHover={{
                            scale: 1.02,
                            boxShadow: "0 8px 25px rgba(255, 215, 0, 0.3)",
                            y: -2
                        }}
                        whileTap={{ scale: 0.98 }}
                        className="bg-brand-gold text-gray-900 px-6 py-3 rounded-lg font-poppins font-semibold text-base w-full hover:bg-yellow-500 hover:shadow-lg transition-all duration-300 relative overflow-hidden"
                    >
                        <motion.span
                            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                            initial={{ x: "-100%" }}
                            whileHover={{ x: "100%" }}
                            transition={{ duration: 0.6 }}
                        />
                        <span className="relative z-10">Enroll Now</span>
                    </motion.button>
                </motion.div>
            </motion.div>
        </motion.div>
    );

    return (
        <section id="courses" className="py-20 bg-gray-50 relative overflow-hidden">
            {/* Background decorative elements */}
            <div className="absolute inset-0 z-0">
                <motion.div
                    animate={{
                        rotate: [0, 360],
                        scale: [1, 1.1, 1],
                    }}
                    transition={{
                        duration: 20,
                        repeat: Infinity,
                        ease: "linear"
                    }}
                    className="absolute top-20 right-20 w-32 h-32 border-2 border-brand-gold/10 rounded-full"
                />
                <motion.div
                    animate={{
                        rotate: [360, 0],
                        scale: [1, 0.9, 1],
                    }}
                    transition={{
                        duration: 15,
                        repeat: Infinity,
                        ease: "linear"
                    }}
                    className="absolute bottom-20 left-20 w-24 h-24 border-2 border-brand-dark-red/10 rounded-full"
                />
            </div>

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
                {/* Enhanced Section Header */}
                <AnimatedSection animation="fadeInUp" className="text-center mb-12">
                    <motion.h2
                        className="text-4xl font-bold text-gray-900 mb-4 font-cinzel"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        Explore Our <motion.span
                            className="text-brand-dark-red"
                            animate={{
                                textShadow: [
                                    "0 0 0px rgba(139, 0, 0, 0)",
                                    "0 0 10px rgba(139, 0, 0, 0.3)",
                                    "0 0 0px rgba(139, 0, 0, 0)"
                                ]
                            }}
                            transition={{
                                duration: 3,
                                repeat: Infinity,
                                ease: "easeInOut"
                            }}
                        >
                            Courses
                        </motion.span>
                    </motion.h2>
                    <motion.p
                        className="text-xl text-gray-600 max-w-3xl mx-auto"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                    >
                        Discover personalized 1-on-1 live classes in Indian music, yoga, and dance.
                        Learn from expert instructors at your own pace with flexible scheduling.
                    </motion.p>
                </AnimatedSection>

                {/* Enhanced Category Filter */}
                <AnimatedSection animation="slideInLeft" delay={0.3} className="flex flex-wrap justify-center gap-4 mb-12">
                    <CategoryButton category="all" label="All Courses" />
                    <CategoryButton category="music" label="Music" />
                    <CategoryButton category="yoga" label="Yoga" />
                    <CategoryButton category="dance" label="Dance" />
                </AnimatedSection>

                {/* Enhanced Course Grid */}
                <AnimatedSection animation="bounceIn" delay={0.5}>
                    <motion.div
                        key={selectedCategory}
                        variants={staggerCards}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                    >
                        {filteredCourses.map((course, index) => (
                            <motion.div
                                key={course.id}
                                variants={{
                                    hidden: { opacity: 0, y: 50, scale: 0.9 },
                                    visible: {
                                        opacity: 1,
                                        y: 0,
                                        scale: 1,
                                        transition: {
                                            type: "spring",
                                            stiffness: 100,
                                            damping: 15
                                        }
                                    }
                                }}
                                layout
                            >
                                <CourseCard course={course} index={index} />
                            </motion.div>
                        ))}
                    </motion.div>
                </AnimatedSection>

                {/* No Courses Message */}
                {filteredCourses.length === 0 && (
                    <AnimatedSection animation="fadeInUp" className="text-center py-12">
                        <motion.div
                            animate={{ rotate: [0, 10, -10, 0] }}
                            transition={{ duration: 2, repeat: Infinity }}
                            className="text-gray-400 mb-4"
                        >
                            <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        </motion.div>
                        <h3 className="text-xl font-medium text-gray-900 mb-2">No courses found</h3>
                        <p className="text-gray-600">
                            No courses match the selected category. Try selecting a different category.
                        </p>
                    </AnimatedSection>
                )}

                {/* Enhanced Bottom CTA */}
                <AnimatedSection animation="fadeInUp" delay={0.8} className="mt-16">
                    <motion.div
                        className="bg-brand-dark-red rounded-2xl p-8 text-white relative overflow-hidden"
                        whileHover={{ scale: 1.02 }}
                        transition={{ duration: 0.3 }}
                    >
                        {/* Background decorative elements */}
                        <motion.div
                            animate={{
                                rotate: [0, 360],
                                scale: [1, 1.2, 1],
                            }}
                            transition={{
                                duration: 10,
                                repeat: Infinity,
                                ease: "linear"
                            }}
                            className="absolute top-4 right-4 w-16 h-16 border-2 border-white/20 rounded-full"
                        />
                        <motion.div
                            animate={{
                                rotate: [360, 0],
                                scale: [1, 0.8, 1],
                            }}
                            transition={{
                                duration: 8,
                                repeat: Infinity,
                                ease: "linear"
                            }}
                            className="absolute bottom-4 left-4 w-12 h-12 border-2 border-brand-gold/30 rounded-full"
                        />

                        <div className="relative z-10 text-center">
                            <motion.h3
                                className="text-2xl font-bold mb-4 font-cinzel"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6 }}
                            >
                                Ready to Start Your Journey?
                            </motion.h3>
                            <motion.p
                                className="text-lg mb-6 opacity-90"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                            >
                                Join thousands of students learning with SwarSadhana's expert instructors
                            </motion.p>
                            <motion.div
                                className="flex flex-col sm:flex-row gap-4 justify-center"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: 0.4 }}
                            >
                                <motion.button
                                    onClick={() => handleEnrollClick('trial')}
                                    whileHover={{
                                        scale: 1.05,
                                        boxShadow: "0 8px 25px rgba(255, 255, 255, 0.2)"
                                    }}
                                    whileTap={{ scale: 0.95 }}
                                    className="bg-white text-brand-dark-red px-6 py-3 rounded-lg font-poppins font-semibold text-base hover:bg-gray-100 transition-all duration-300"
                                >
                                    Start Free Trial
                                </motion.button>
                                <motion.button
                                    onClick={() => {
                                        const contactSection = document.getElementById('contact');
                                        if (contactSection) {
                                            contactSection.scrollIntoView({ behavior: 'smooth' });
                                        }
                                    }}
                                    whileHover={{
                                        scale: 1.05,
                                        boxShadow: "0 8px 25px rgba(255, 215, 0, 0.3)"
                                    }}
                                    whileTap={{ scale: 0.95 }}
                                    className="bg-brand-gold text-gray-900 px-6 py-3 rounded-lg font-poppins font-semibold text-base hover:bg-yellow-500 transition-all duration-300"
                                >
                                    Contact Us
                                </motion.button>
                            </motion.div>
                        </div>
                    </motion.div>
                </AnimatedSection>
            </div>
        </section>
    );
};

export default Courses;
